// Third-party services on the pages of the Medsender iOS app's sign-in (isIosAppSignInFlowPage:
// its printer sign-in page, and the login, Forgot password and Microsoft sign-in return on their
// way to it). There no third-party script may load or boot and no account data may leave to third
// parties. Gibraltar's sign-in pages are ordinary pages.
//
// - Intercom, GrowSurf and ProfitWell are page scripts in layouts/application.html.erb. When the
//   page load begins on an iOS app sign-in page the server holds them back in an inert <template>;
//   they load here once the SPA reaches an ordinary page, so that page keeps its usual widgets.
// - PostHog starts at that same moment, and drops whatever it would capture while the SPA is on an
//   iOS app sign-in page (a load that began on an ordinary page has already started it).
// - Intercom's boot and ProfitWell's user_id (containers/App/thirdPartyTracking.js) ask
//   thirdPartyWidgets, which has none to offer on those pages.
import { isIosAppSignInFlowPage } from './appSignIn';

export const DEFERRED_THIRD_PARTY_SCRIPTS_ID = 'm8-deferred-third-party-scripts';

/**
 * Loads the page scripts the server held back, once. A script parsed inside a <template> never
 * runs, so each one is recreated (attributes and source) in <head>, where it runs as the layout's
 * copy would have.
 *
 * @param {Document} doc
 * @returns {boolean} whether there were scripts to load
 */
export const loadDeferredThirdPartyScripts = (doc = document) => {
  const template = doc.getElementById(DEFERRED_THIRD_PARTY_SCRIPTS_ID);
  if (!template) return false;

  template.remove();
  Array.from(template.content.querySelectorAll('script')).forEach((held) => {
    const script = doc.createElement('script');
    Array.from(held.attributes).forEach(({ name, value }) => script.setAttribute(name, value));
    script.text = held.textContent;
    doc.head.appendChild(script);
  });
  return true;
};

/**
 * PostHog's before_send: nothing is captured while the page is an iOS app sign-in page.
 *
 * @param {object} event
 * @returns {object|null}
 */
export const dropOnAppSignInPages = (event) => (
  isIosAppSignInFlowPage(window.location) ? null : event
);

/**
 * Loads the held-back page scripts and starts analytics on the first ordinary page of this page
 * load: now, or after a later navigation.
 *
 * @param {{ history: object, startAnalytics?: Function, doc?: Document }} options
 * @returns {Function} stops listening
 */
export const startThirdPartyServices = ({ history, startAnalytics = () => {}, doc = document }) => {
  // The server held the scripts back, so this load began on an iOS app sign-in page: stay there
  // until the SPA navigates, even if this page's query reads differently here.
  let heldByServer = Boolean(doc.getElementById(DEFERRED_THIRD_PARTY_SCRIPTS_ID));
  let analyticsStarted = false;

  const update = (location) => {
    if (heldByServer || isIosAppSignInFlowPage(location)) return;
    loadDeferredThirdPartyScripts(doc);
    if (!analyticsStarted) {
      analyticsStarted = true;
      startAnalytics();
    }
  };

  update(history.location);
  return history.listen((location) => {
    heldByServer = false;
    update(location);
  });
};

/**
 * The Intercom and ProfitWell functions this page may call: none on an iOS app sign-in page, and
 * only those whose scripts are loaded.
 *
 * @param {{ pathname: string, search: string }} location
 * @param {Window} win
 * @returns {{ intercom: Function|null, profitwell: Function|null }}
 */
export const thirdPartyWidgets = (location = window.location, win = window) => {
  if (isIosAppSignInFlowPage(location)) return { intercom: null, profitwell: null };
  return {
    intercom: typeof win.Intercom === 'function' ? win.Intercom : null,
    profitwell: typeof win.profitwell === 'function' ? win.profitwell : null,
  };
};
